// ✅ src/screens/MeetupsScreen.js (FULL)
// Same logic + pagination (2 cursors) + NO DEBUG UI

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  where,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import useSubscription from "../hooks/useSubscription";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function lower(v) {
  return safeStr(v).toLowerCase();
}
function num(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function calcAgeFromBirthDate(birthDateTs) {
  const ms = msFromTs(birthDateTs);
  if (!ms) return null;
  const dob = new Date(ms);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function normalizeGoal(v) {
  return safeStr(v).toLowerCase();
}

/* ---------------- UI bits ---------------- */
function Pill({ children }) {
  return <span style={S.pill}>{children}</span>;
}

function BigCard({ person }) {
  const name = safeStr(person?.displayName) || "Unknown";
  const avatar = safeStr(person?.avatar) || safeStr(person?.photos?.[0]);
  const city = safeStr(person?.city) || "Nearby";
  const age = Number.isFinite(Number(person?.age)) ? Number(person.age) : null;

  return (
    <div style={S.bigCard}>
      <div style={S.bigMedia}>
        {avatar ? (
          <img src={avatar} alt={name} style={S.bigImg} />
        ) : (
          <div style={S.bigFallback}>{initials(name)}</div>
        )}

        <div style={S.bigOverlay}>
          <div style={S.bigName}>
            {name}
            {age !== null ? <span style={{ opacity: 0.85 }}> · {age}</span> : null}
          </div>

          <div style={S.bigMeta}>
            <Pill>📍 {city}</Pill>
            {person?.verified ? <Pill>✅ Verified</Pill> : <Pill>✨ New</Pill>}
            <Pill>🔥 Meetups</Pill>
          </div>

          {safeStr(person?.bio) ? <div style={S.bigBio}>{safeStr(person.bio)}</div> : null}
        </div>
      </div>
    </div>
  );
}

function UpgradeOverlay({ plan, onUpgrade, onBack }) {
  return (
    <div style={S.gateWrap} role="dialog" aria-modal="true">
      <div style={S.gateCard}>
        <div style={S.gateTitle}>Please upgrade</div>
        <div style={S.gateText}>
          Meetups is a premium feature.
          <div style={{ marginTop: 6, opacity: 0.8 }}>
            Current plan: <b>{plan || "Free"}</b>
          </div>
        </div>

        <div style={S.gateBtns}>
          <button type="button" style={S.gateBtnPrimary} onClick={onUpgrade}>
            Upgrade now
          </button>
          <button type="button" style={S.gateBtnGhost} onClick={onBack}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- main ---------------- */
export default function MeetupsScreen() {
  const nav = useNavigate();

  const { loading: subLoading, allowed, plan, err } = useSubscription();
  const gated = !subLoading && !allowed;

  const PAGE_SIZE = 150;
  const AUTO_LOAD_WHEN_LEFT = 3;

  const [me, setMe] = useState(null);

  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  // ✅ FIX: two cursors (one for each query)
  const [cursorLower, setCursorLower] = useState(null); // for "meetups"
  const [cursorCap, setCursorCap] = useState(null); // for "Meetups"

  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [matchToast, setMatchToast] = useState(null); // { name, uid, avatar }

  const current = deck[index] || null;

  const title = useMemo(() => {
    const c = safeStr(me?.city);
    return c ? `Meetups in ${c}` : "Meetups Nearby";
  }, [me]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }

      setLoading(true);
      setDeck([]);
      setIndex(0);
      setCursorLower(null);
      setCursorCap(null);
      setHasMore(true);

      try {
        const mySnap = await getDoc(doc(db, "datingProfiles", u.uid));
        const myProfile = mySnap.exists() ? mySnap.data() : null;

        const nextMe = {
          uid: u.uid,
          displayName: safeStr(myProfile?.displayName) || "Me",
          city: safeStr(myProfile?.city),
          avatar: safeStr(myProfile?.avatar) || safeStr(myProfile?.photos?.[0]),
          relationshipGoal: normalizeGoal(myProfile?.relationshipGoal),

          lookingFor: lower(myProfile?.lookingFor) || "everyone",
          prefAgeMin: num(myProfile?.prefAgeMin, 18) ?? 18,
          prefAgeMax: num(myProfile?.prefAgeMax, null),
        };

        setMe(nextMe);

        await loadMore(u.uid, {
          lookingFor: nextMe.lookingFor,
          prefAgeMin: nextMe.prefAgeMin,
          prefAgeMax: nextMe.prefAgeMax,
          reset: true,
        });
      } catch (e) {
        console.error(e);
        setDeck([]);
        setIndex(0);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  async function loadMore(myUid, prefs) {
    const uid = safeStr(myUid);
    if (!uid) return;
    if (loadingMore) return;
    if (!hasMore && !prefs?.reset) return;

    setLoadingMore(true);

    try {
      const swipedIds = new Set();
      try {
        const swipeSnap = await getDocs(
          query(collection(db, "datingProfiles", uid, "swipes"), limit(600))
        );
        swipeSnap.forEach((d) => swipedIds.add(d.id));
      } catch {}

      const baseLower = [
        collection(db, "datingProfiles"),
        where("relationshipGoal", "==", "meetups"),
        orderBy("lastActiveAt", "desc"),
        limit(PAGE_SIZE),
      ];

      const baseCap = [
        collection(db, "datingProfiles"),
        where("relationshipGoal", "==", "Meetups"),
        orderBy("lastActiveAt", "desc"),
        limit(PAGE_SIZE),
      ];

      const qLower =
        prefs?.reset || !cursorLower ? query(...baseLower) : query(...baseLower, startAfter(cursorLower));

      const qCap =
        prefs?.reset || !cursorCap ? query(...baseCap) : query(...baseCap, startAfter(cursorCap));

      const [sLower, sCap] = await Promise.all([getDocs(qLower), getDocs(qCap)]);

      const lastLower = sLower.docs?.[sLower.docs.length - 1] || null;
      const lastCap = sCap.docs?.[sCap.docs.length - 1] || null;

      if (lastLower) setCursorLower(lastLower);
      if (lastCap) setCursorCap(lastCap);

      const gotLower = sLower.docs?.length || 0;
      const gotCap = sCap.docs?.length || 0;
      const gotCount = gotLower + gotCap;

      console.log("[Meetups] fetched", { gotLower, gotCap, gotCount });

      if (gotCount === 0) {
        setHasMore(false);
        return;
      }

      // heuristic: if both small we may be near end
      const more = gotLower === PAGE_SIZE || gotCap === PAGE_SIZE;
      setHasMore(more);

      const lookingFor = lower(prefs?.lookingFor || me?.lookingFor || "everyone");
      const prefMin = num(prefs?.prefAgeMin ?? me?.prefAgeMin, 18) ?? 18;
      const prefMax = num(prefs?.prefAgeMax ?? me?.prefAgeMax, null);

      const byId = new Map();

      const addDocRow = (d) => {
        if (d.id === uid) return;
        if (swipedIds.has(d.id)) return;

        const data = d.data() || {};
        const targetGender = lower(data.gender);
        const computed = calcAgeFromBirthDate(data?.birthDate);
        const ageVal = Number.isFinite(Number(data?.age)) ? Number(data.age) : computed;

        // gender filter
        if (lookingFor !== "everyone") {
          if (!targetGender) return;
          if (targetGender !== lookingFor) return;
        }

        // age filter
        if (!Number.isFinite(Number(ageVal))) return;
        if (Number(ageVal) < Number(prefMin)) return;
        if (prefMax != null && Number(ageVal) > Number(prefMax)) return;

        byId.set(d.id, {
          uid: d.id,
          displayName: safeStr(data.displayName),
          avatar: safeStr(data.avatar),
          photos: Array.isArray(data.photos) ? data.photos : [],
          age: Number(ageVal),
          verified: Boolean(data.verified),
          city: safeStr(data.city),
          bio: safeStr(data.bio || data.about || data.description),
          relationshipGoal: safeStr(data.relationshipGoal),
        });
      };

      sLower.forEach(addDocRow);
      sCap.forEach(addDocRow);

      const incoming = Array.from(byId.values());

      console.log("[Meetups] after filters:", {
        incoming: incoming.length,
        lookingFor,
        prefMin,
        prefMax,
      });

      setDeck((prev) => {
        const seen = new Set((prev || []).map((p) => p.uid));
        const merged = prefs?.reset ? [] : [...(prev || [])];
        for (const p of incoming) {
          if (!seen.has(p.uid)) merged.push(p);
        }
        return merged;
      });

      if (prefs?.reset) setIndex(0);
    } catch (e) {
      console.error("meetups loadMore error", e);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    const uid = safeStr(me?.uid);
    if (!uid) return;
    const left = (deck?.length || 0) - index;
    if (left <= AUTO_LOAD_WHEN_LEFT && hasMore && !loadingMore) {
      loadMore(uid, {
        lookingFor: me?.lookingFor,
        prefAgeMin: me?.prefAgeMin,
        prefAgeMax: me?.prefAgeMax,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, deck.length, hasMore, loadingMore, me?.uid]);

  async function writeSwipe({ action, target }) {
    const u = auth.currentUser;
    if (!u || !target?.uid) return;

    const myUid = u.uid;
    const otherUid = target.uid;

    await runTransaction(db, async (tx) => {
      const swipeRef = doc(db, "datingProfiles", myUid, "swipes", otherUid);
      const likeSentRef = doc(db, "datingProfiles", myUid, "likesSent", otherUid);
      const likeRecvRef = doc(db, "datingProfiles", otherUid, "likesReceived", myUid);

      tx.set(swipeRef, { action, createdAt: serverTimestamp() }, { merge: true });

      if (action === "like") {
        tx.set(likeSentRef, { createdAt: serverTimestamp() }, { merge: true });
        tx.set(likeRecvRef, { createdAt: serverTimestamp() }, { merge: true });

        const otherLikedMeRef = doc(db, "datingProfiles", myUid, "likesReceived", otherUid);
        const otherLikedMeSnap = await tx.get(otherLikedMeRef);

        if (otherLikedMeSnap.exists()) {
          const matchA = doc(db, "datingProfiles", myUid, "matches", otherUid);
          const matchB = doc(db, "datingProfiles", otherUid, "matches", myUid);
          const payload = { matchedAt: serverTimestamp(), uidA: myUid, uidB: otherUid };
          tx.set(matchA, payload, { merge: true });
          tx.set(matchB, payload, { merge: true });
        }
      }
    });
  }

  async function onPass() {
    if (!current || acting) return;
    setActing(true);
    try {
      await writeSwipe({ action: "pass", target: current });
    } catch (e) {
      console.error("pass error", e);
    } finally {
      setActing(false);
      setIndex((i) => i + 1);
    }
  }

  async function onLike() {
    if (!current || acting) return;
    setActing(true);
    const liked = current;

    try {
      const u = auth.currentUser;
      if (u) {
        const checkRef = doc(db, "datingProfiles", u.uid, "likesReceived", liked.uid);
        const checkSnap = await getDoc(checkRef);
        const isMatch = checkSnap.exists();

        await writeSwipe({ action: "like", target: liked });

        if (isMatch) {
          setMatchToast({
            uid: liked.uid,
            name: safeStr(liked.displayName) || "Someone",
            avatar: safeStr(liked.avatar) || safeStr(liked.photos?.[0]),
          });
          setTimeout(() => setMatchToast(null), 3000);
        }
      } else {
        await writeSwipe({ action: "like", target: liked });
      }
    } catch (e) {
      console.error("like error", e);
    } finally {
      setActing(false);
      setIndex((i) => i + 1);
    }
  }

  if (loading) {
    return (
      <div style={S.loading}>
        Loading meetups…
        {subLoading ? <div style={{ marginTop: 10, opacity: 0.7 }}>Checking subscription…</div> : null}
      </div>
    );
  }

  return (
    <div style={S.page}>
      {!subLoading && err ? (
        <div style={S.warnBox}>
          <div style={S.warnTitle}>Subscription read error</div>
          <div style={S.warnText}>{err}</div>
        </div>
      ) : null}

      <div style={{ ...(gated ? S.blurred : null) }}>
        <div style={S.shell}>
          <div style={S.topBar}>
            <button type="button" style={S.backBtn} onClick={() => nav("/profile")} aria-label="Back" title="Back">
              ‹
            </button>

            <div style={{ minWidth: 0 }}>
              <div style={S.topTitle}>{title}</div>
              <div style={S.topSub}>
                {Math.max(0, deck.length - index)} people left • Swipe to decide
              </div>
            </div>
          </div>

          <div style={{ width: "min(640px, 100%)", margin: "0 auto 12px", display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              style={S.secondaryPillBtn}
              disabled={loadingMore}
              onClick={() => {
                const uid = safeStr(me?.uid);
                if (!uid) return;
                loadMore(uid, {
                  lookingFor: me?.lookingFor,
                  prefAgeMin: me?.prefAgeMin,
                  prefAgeMax: me?.prefAgeMax,
                });
              }}
              title="Load next 150"
            >
              {loadingMore ? "⏳ Loading..." : "♻️ Refresh (next 150)"}
            </button>
          </div>

          {current ? (
            <div style={S.stage}>
              <BigCard person={current} />

              <div style={S.actions}>
                <button type="button" style={{ ...S.actionBtn, ...S.noBtn }} onClick={onPass} disabled={acting} aria-label="Pass" title="Pass">
                  ❌
                </button>

                <button type="button" style={{ ...S.actionBtn, ...S.likeBtn }} onClick={onLike} disabled={acting} aria-label="Like" title="Like">
                  ❤️
                </button>
              </div>

              <div style={S.hint}>Tip: ❤️ = Like • ❌ = Pass • If they liked you too → Match</div>
            </div>
          ) : (
            <div style={S.empty}>
              {hasMore || loadingMore ? "Loading more meetup-ready people…" : "No more meetup-ready people right now 🥲"}
              <div style={{ marginTop: 10, opacity: 0.8 }}>
                Try refresh to load the next batch, or invite more users to set goal to <b>meetups</b>.
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={S.primaryBtn}
                  disabled={loadingMore || !hasMore}
                  onClick={() => {
                    const uid = safeStr(me?.uid);
                    if (!uid) return;
                    loadMore(uid, {
                      lookingFor: me?.lookingFor,
                      prefAgeMin: me?.prefAgeMin,
                      prefAgeMax: me?.prefAgeMax,
                    });
                  }}
                >
                  {loadingMore ? "Loading..." : hasMore ? "Load next 150" : "No more"}
                </button>

                <button type="button" style={S.ghostBtn} onClick={() => nav("/profile")}>
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>

        {matchToast ? (
          <div style={S.toastWrap} role="status" aria-live="polite">
            <div style={S.toast}>
              <div style={S.toastTitle}>🎉 It’s a match!</div>
              <div style={S.toastRow}>
                {matchToast.avatar ? (
                  <img src={matchToast.avatar} alt="" style={S.toastAvatar} />
                ) : (
                  <div style={S.toastAvatarFallback}>{initials(matchToast.name)}</div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={S.toastName}>{matchToast.name}</div>
                  <div style={S.toastSub}>You both liked each other.</div>
                </div>
              </div>

              <div style={S.toastBtns}>
                <button type="button" style={S.toastBtn} onClick={() => { setMatchToast(null); nav("/inbox"); }}>
                  Open inbox
                </button>
                <button type="button" style={S.toastBtnAlt} onClick={() => setMatchToast(null)}>
                  Continue swiping
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {gated ? <UpgradeOverlay plan={plan} onUpgrade={() => nav("/upgrade")} onBack={() => nav(-1)} /> : null}
      {subLoading ? <div style={S.loadingFloat}>Checking subscription…</div> : null}
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 650px at 18% 0%, rgba(236,72,153,.14), transparent 60%), " +
      "radial-gradient(900px 520px at 90% 18%, rgba(99,102,241,.14), transparent 55%), " +
      "linear-gradient(180deg,#fbfaf8 0%, #f7f2ee 55%, #fbf7f3 100%)",
    color: "#0f172a",
    paddingBottom: 26,
  },

  blurred: { filter: "blur(10px)", transform: "scale(0.98)", opacity: 0.75, pointerEvents: "none" },

  shell: { width: "100%", maxWidth: 980, margin: "0 auto", padding: "18px 12px 24px" },
  loading: { minHeight: "70vh", display: "grid", placeItems: "center", padding: 30, color: "#111827", fontWeight: 900 },

  topBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 14,
    borderRadius: 22,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 18px 50px rgba(2,6,23,0.08)",
    backdropFilter: "blur(12px)",
    marginBottom: 14,
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    fontSize: 22,
    fontWeight: 900,
    flexShrink: 0,
  },
  topTitle: { fontWeight: 1000, letterSpacing: -0.4, fontSize: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  topSub: { fontWeight: 800, opacity: 0.65, fontSize: 13 },

  secondaryPillBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.78)",
    borderRadius: 999,
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 1000,
    boxShadow: "0 12px 28px rgba(2,6,23,0.08)",
  },

  stage: { display: "grid", gap: 14, justifyItems: "center" },

  bigCard: {
    width: "min(640px, 100%)",
    borderRadius: 28,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 22px 70px rgba(2,6,23,0.10)",
    overflow: "hidden",
  },
  bigMedia: { position: "relative" },
  bigImg: { width: "100%", height: 520, objectFit: "cover", display: "block" },
  bigFallback: {
    width: "100%",
    height: 520,
    display: "grid",
    placeItems: "center",
    background: "rgba(2,6,23,0.85)",
    color: "#fff",
    fontWeight: 1000,
    fontSize: 44,
  },
  bigOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,.50) 35%, rgba(0,0,0,.78) 100%)",
    color: "#fff",
  },
  bigName: { fontWeight: 1000, fontSize: 22, letterSpacing: -0.3 },
  bigMeta: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
  bigBio: { marginTop: 10, fontWeight: 800, opacity: 0.9, lineHeight: 1.35, maxWidth: 560 },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 900,
    fontSize: 12,
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.22)",
    whiteSpace: "nowrap",
  },

  actions: { display: "flex", gap: 16, alignItems: "center", justifyContent: "center", marginTop: 2 },
  actionBtn: {
    width: 62,
    height: 62,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    cursor: "pointer",
    fontSize: 26,
    fontWeight: 1000,
    boxShadow: "0 18px 50px rgba(2,6,23,0.12)",
    background: "rgba(255,255,255,0.78)",
  },
  noBtn: { background: "rgba(255,255,255,0.80)" },
  likeBtn: { background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))", color: "#fff", border: "none" },

  hint: { fontWeight: 900, opacity: 0.65, fontSize: 13, textAlign: "center", paddingBottom: 6 },

  empty: {
    padding: 16,
    borderRadius: 18,
    border: "1px dashed rgba(2,6,23,0.18)",
    background: "rgba(255,255,255,0.65)",
    fontWeight: 900,
    opacity: 0.95,
    maxWidth: 720,
    margin: "0 auto",
  },
  primaryBtn: {
    border: "none",
    borderRadius: 999,
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#fff",
    background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))",
    boxShadow: "0 14px 34px rgba(99,102,241,0.25)",
  },
  ghostBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    borderRadius: 999,
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 1000,
    background: "rgba(255,255,255,0.75)",
  },

  toastWrap: { position: "fixed", inset: 0, display: "grid", placeItems: "center", background: "rgba(2,6,23,0.45)", padding: 14, zIndex: 9999 },
  toast: { width: "min(520px, 100%)", borderRadius: 22, border: "1px solid rgba(2,6,23,0.10)", background: "rgba(255,255,255,0.92)", boxShadow: "0 30px 90px rgba(2,6,23,0.22)", padding: 14 },
  toastTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.2 },
  toastRow: { display: "flex", gap: 12, alignItems: "center", marginTop: 10 },
  toastAvatar: { width: 56, height: 56, borderRadius: 16, objectFit: "cover", border: "1px solid rgba(2,6,23,0.10)" },
  toastAvatarFallback: { width: 56, height: 56, borderRadius: 16, display: "grid", placeItems: "center", background: "rgba(2,6,23,0.85)", color: "#fff", fontWeight: 1000 },
  toastName: { fontWeight: 1000, fontSize: 16 },
  toastSub: { fontWeight: 900, opacity: 0.7, fontSize: 13, marginTop: 2 },
  toastBtns: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12, flexWrap: "wrap" },
  toastBtn: { border: "none", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, color: "#fff", background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))" },
  toastBtnAlt: { border: "1px solid rgba(2,6,23,0.10)", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, background: "rgba(255,255,255,0.80)" },

  gateWrap: { position: "fixed", inset: 0, display: "grid", placeItems: "center", padding: 14, background: "rgba(2,6,23,0.45)", zIndex: 99999 },
  gateCard: { width: "min(520px, 100%)", borderRadius: 22, border: "1px solid rgba(2,6,23,0.10)", background: "rgba(255,255,255,0.92)", boxShadow: "0 30px 90px rgba(2,6,23,0.22)", padding: 14 },
  gateTitle: { fontWeight: 1000, fontSize: 18, letterSpacing: -0.2 },
  gateText: { marginTop: 8, fontWeight: 900, opacity: 0.75, lineHeight: 1.35 },
  gateBtns: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" },
  gateBtnPrimary: { border: "none", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, color: "#fff", background: "linear-gradient(135deg, rgba(236,72,153,1), rgba(99,102,241,1))" },
  gateBtnGhost: { border: "1px solid rgba(2,6,23,0.10)", borderRadius: 999, padding: "10px 12px", cursor: "pointer", fontWeight: 1000, background: "rgba(255,255,255,0.80)" },

  loadingFloat: { position: "fixed", bottom: 14, left: "50%", transform: "translateX(-50%)", padding: "10px 12px", borderRadius: 999, border: "1px solid rgba(2,6,23,0.10)", background: "rgba(255,255,255,0.92)", fontWeight: 1000, boxShadow: "0 14px 34px rgba(2,6,23,0.18)", zIndex: 99999 },

  warnBox: { width: "min(980px, calc(100% - 24px))", margin: "14px auto 0", borderRadius: 16, border: "1px solid rgba(239,68,68,0.25)", background: "rgba(239,68,68,0.07)", padding: 12 },
  warnTitle: { fontWeight: 1000, marginBottom: 4 },
  warnText: { fontWeight: 900, opacity: 0.8, fontSize: 13, wordBreak: "break-word" },
};